import { useEffect, useRef, useState } from "react";
import { db, type Visit } from "../db";
import { apiBlob } from "../api";

// Просмотр фотографий визита — в карточке точки, без открытия формы.
//
// Снимки живут в двух местах: снятые на ЭТОМ устройстве лежат в его памяти
// (видны и офлайн), снятые на другом — только на сервере, их качаем при связи.
// Сколько всего фото, известно без сети: список серверных приезжает вместе
// с визитом при обмене.
//
// У старых визитов фото не грузятся сами — по кнопке. Каждый снимок ~300 КБ,
// и высосать мегабайты мобильного трафика ради пролистанной истории обидно.
// У последнего визита наоборот: он главное, ради чего открывают точку.

type Tile = { id: string; url?: string; error?: boolean };

export default function VisitPhotos({ visit, eager = false }: { visit: Visit; eager?: boolean }) {
  const [count, setCount] = useState(0);
  const [tiles, setTiles] = useState<Tile[] | null>(null); // null — ещё не грузили
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const urls = useRef<string[]>([]);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    setTiles(null);
    void (async () => {
      const local = await db.photos.where("visitId").equals(visit.id).toArray();
      const remote = (visit.photos ?? []).filter((r) => !local.some((l) => l.id === r.id));
      if (alive.current) setCount(local.length + remote.length);
    })();
    return () => { alive.current = false; };
  }, [visit.id, visit.updatedAt]);

  // Ссылки на картинки создаются руками — руками же и освобождаем,
  // иначе каждая открытая карточка оставляет за собой снимки в памяти.
  useEffect(() => () => { urls.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  async function load() {
    if (busy) return;
    setBusy(true);
    const keep = (blob: Blob) => {
      const url = URL.createObjectURL(blob);
      urls.current.push(url);
      return url;
    };
    const local = await db.photos.where("visitId").equals(visit.id).toArray();
    const out: Tile[] = local.map((l) => ({ id: l.id, url: keep(l.blob) }));
    if (!alive.current) return;
    setTiles([...out]);

    const seen = new Set(local.map((l) => l.id));
    for (const r of visit.photos ?? []) {
      if (seen.has(r.id)) continue;
      try {
        out.push({ id: r.id, url: keep(await apiBlob("/api/photos/" + r.file)) });
      } catch {
        out.push({ id: r.id, error: true });
      }
      if (!alive.current) return;
      setTiles([...out]);
    }
    setBusy(false);
  }

  useEffect(() => {
    if (eager && count > 0 && tiles === null) void load();
  }, [eager, count]); // eslint-disable-line react-hooks/exhaustive-deps

  if (count === 0) return null;

  return (
    <div className="vphotos">
      {tiles === null && !eager ? (
        <button className="btn tiny" onClick={() => void load()}>
          📷 {count} фото — показать
        </button>
      ) : (
        <div className="photos">
          {(tiles ?? []).map((t) =>
            t.url ? (
              <img key={t.id} className="photo tap" src={t.url} alt=""
                onClick={() => setOpen(t.url!)} />
            ) : (
              <span key={t.id} className="photoerr">нет связи — фото на сервере</span>
            )
          )}
          {busy && <span className="photoerr">загружаю…</span>}
        </div>
      )}

      {open && (
        <div className="viewer" onClick={() => setOpen(null)}>
          <img src={open} alt="" />
        </div>
      )}
    </div>
  );
}
